import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Checkbox,
  Code,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Image,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Progress,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  ChevronLeft,
  ChevronRight,
  Edit3,
  Eye,
  ExternalLink,
  ImagePlus,
  Play,
  RefreshCcw,
  Search,
  Star,
  Trash2,
  Upload,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../auth/AuthContext";
import GlobalSpinner from "../../components/CustomReusables/GlobalSpinner";
import {
  bulkDeleteMarketplaceProducts,
  bulkUpdateMarketplaceProducts,
  editMarketplaceProduct,
  getMarketplaceListings,
  getMarketplaceRun,
  getMarketplaceStatus,
  MarketplaceListing,
  MarketplaceReconcileResult,
  MarketplaceRun,
  MarketplaceSourceStatus,
  MarketplaceSyncResult,
  MarketplaceVerificationStatus,
  reconcileMarketplace,
  searchAndSyncMarketplace,
} from "./marketplaceApi";

type MarketplaceDiscoverySource = "mock_public" | "openai_web";

type MarketplaceSourceFilter =
  | ""
  | "mock_public"
  | "openai_web"
  | "public_page";

type RunGroupKey = "today" | "this_week" | "last_week" | "older";

type ProductStatus = "active" | "out_of_stock" | "archived" | "draft";

interface ProductEditForm {
  title: string;
  description: string;
  brand: string;
  category: string;
  price: number;
  stockQty: number;
  status: ProductStatus;
  isFeatured: boolean;
}

interface SelectedEditImage {
  id: string;
  file: File;
  previewUrl: string;
}

interface RunGroup {
  key: RunGroupKey;
  label: string;
  runs: MarketplaceRun[];
}

const FALLBACK_PRODUCT_IMAGE = "/images/misc/product-placeholder.png";
const MAX_EDIT_IMAGES = 8;
const MAX_EDIT_IMAGE_BYTES = 12 * 1024 * 1024;
const ACCEPTED_EDIT_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/avif",
]);

const DEFAULT_QUERIES = [
  "Wireless Bluetooth Headphones",
  "Portable Bluetooth Speaker",
].join("\n");

const EMPTY_EDIT_FORM: ProductEditForm = {
  title: "",
  description: "",
  brand: "",
  category: "",
  price: 0,
  stockQty: 0,
  status: "active",
  isFeatured: false,
};

const formatDate = (value?: string | null): string => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const formatMoney = (value?: number | null, currency = "ZAR"): string => {
  if (value === undefined || value === null || !Number.isFinite(value)) {
    return "—";
  }

  try {
    return new Intl.NumberFormat(currency === "MWK" ? "en-MW" : "en-ZA", {
      style: "currency",
      currency,
      maximumFractionDigits: currency === "MWK" ? 0 : 2,
      minimumFractionDigits: currency === "MWK" ? 0 : 2,
    }).format(value);
  } catch {
    return `${currency} ${value}`;
  }
};

const getErrorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error ? error.message : fallback;

const getSelectedImageId = (file: File): string =>
  `${file.name}-${file.size}-${file.lastModified}`;

const getListingImages = (listing: MarketplaceListing | null): string[] => {
  if (!listing) {
    return [];
  }

  const images = [
    ...(listing.product?.images || []),
    listing.product?.imageUrl,
    listing.product?.thumbnail,
    listing.sourceImageUrl,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);

  return Array.from(new Set(images));
};

const getListingTitle = (listing: MarketplaceListing): string =>
  listing.product?.title || listing.originalTitle || "Untitled product";

const getListingDescription = (listing: MarketplaceListing): string =>
  listing.product?.description ||
  listing.originalDescription ||
  "No description available.";

const getListingPrice = (
  listing: MarketplaceListing,
): { value?: number | null; currency: string; label: string } => {
  if (listing.product?.price !== undefined && listing.product?.price !== null) {
    return {
      value: listing.product.price,
      currency: "MWK",
      label: "PickDengu price",
    };
  }

  return {
    value: listing.observedPrice,
    currency: listing.observedCurrency || "ZAR",
    label: "Source price",
  };
};

const getAvailabilityColor = (availability: string): string => {
  if (availability === "in_stock") {
    return "green";
  }

  if (availability === "out_of_stock") {
    return "red";
  }

  if (availability === "preorder") {
    return "blue";
  }

  return "orange";
};

const getVerificationColor = (status: string): string => {
  if (status === "verified") {
    return "green";
  }

  if (status === "discovered") {
    return "blue";
  }

  if (status === "unavailable" || status === "blocked") {
    return "red";
  }

  return "orange";
};

const getRunStatusColor = (status: string): string => {
  if (status === "completed") {
    return "green";
  }

  if (status === "failed") {
    return "red";
  }

  if (status === "running") {
    return "blue";
  }

  return "gray";
};

const startOfDay = (value: Date): Date => {
  const result = new Date(value);
  result.setHours(0, 0, 0, 0);
  return result;
};

const startOfWeek = (value: Date): Date => {
  const result = startOfDay(value);
  const day = result.getDay();
  const daysSinceMonday = (day + 6) % 7;
  result.setDate(result.getDate() - daysSinceMonday);
  return result;
};

const groupRunsByPeriod = (runs: MarketplaceRun[]): RunGroup[] => {
  const now = new Date();
  const todayStart = startOfDay(now);
  const thisWeekStart = startOfWeek(now);
  const lastWeekStart = new Date(thisWeekStart);
  lastWeekStart.setDate(lastWeekStart.getDate() - 7);

  const groups: Record<RunGroupKey, MarketplaceRun[]> = {
    today: [],
    this_week: [],
    last_week: [],
    older: [],
  };

  [...runs]
    .sort((left, right) => {
      const leftDate = new Date(left.createdAt || 0).getTime();
      const rightDate = new Date(right.createdAt || 0).getTime();
      return rightDate - leftDate;
    })
    .forEach((run) => {
      const createdAt = new Date(run.createdAt || 0);

      if (Number.isNaN(createdAt.getTime())) {
        groups.older.push(run);
        return;
      }

      if (createdAt >= todayStart) {
        groups.today.push(run);
      } else if (createdAt >= thisWeekStart) {
        groups.this_week.push(run);
      } else if (createdAt >= lastWeekStart) {
        groups.last_week.push(run);
      } else {
        groups.older.push(run);
      }
    });

  const orderedGroups: RunGroup[] = [
    { key: "today", label: "Today", runs: groups.today },
    { key: "this_week", label: "Earlier this week", runs: groups.this_week },
    { key: "last_week", label: "Last week", runs: groups.last_week },
    { key: "older", label: "Older", runs: groups.older },
  ];

  return orderedGroups.filter((group) => group.runs.length > 0);
};

const getUpdatedCount = (result: unknown): number => {
  const value = result as {
    updated?: number;
    modified?: number;
    modifiedCount?: number;
  };

  return Number(value?.updated ?? value?.modified ?? value?.modifiedCount ?? 0);
};

const ProductImageButton = ({
  listing,
  size = "68px",
  onOpen,
}: {
  listing: MarketplaceListing;
  size?: string | Record<string, string>;
  onOpen: () => void;
}) => {
  const images = getListingImages(listing);
  const displayImage = images[0] || FALLBACK_PRODUCT_IMAGE;

  return (
    <Box
      as="button"
      type="button"
      role="group"
      position="relative"
      flexShrink={0}
      w={size}
      h={size}
      borderRadius="xl"
      overflow="hidden"
      bg="gray.50"
      borderWidth="1px"
      cursor="pointer"
      onClick={onOpen}
      aria-label={`View ${getListingTitle(listing)}`}
      _focusVisible={{
        outline: "2px solid",
        outlineColor: "teal.400",
        outlineOffset: "2px",
      }}
    >
      <Image
        src={displayImage}
        fallbackSrc={FALLBACK_PRODUCT_IMAGE}
        alt={getListingTitle(listing)}
        w="100%"
        h="100%"
        objectFit="contain"
      />

      <Flex
        position="absolute"
        inset={0}
        align="center"
        justify="center"
        bg="blackAlpha.400"
        opacity={{ base: 1, md: 0 }}
        transition="opacity 0.2s ease"
        _groupHover={{ opacity: 1 }}
      >
        <Flex
          align="center"
          justify="center"
          w="34px"
          h="34px"
          borderRadius="full"
          bg="whiteAlpha.800"
          color="gray.800"
          backdropFilter="blur(8px)"
          boxShadow="sm"
        >
          <Eye size={17} />
        </Flex>
      </Flex>

      {images.length > 1 && (
        <Badge
          position="absolute"
          right="5px"
          bottom="5px"
          borderRadius="full"
          bg="blackAlpha.700"
          color="white"
          fontSize="9px"
          px={1.5}
        >
          {images.length}
        </Badge>
      )}
    </Box>
  );
};

const ListingBadges = ({ listing }: { listing: MarketplaceListing }) => (
  <HStack spacing={1.5} flexWrap="wrap">
    <Badge
      colorScheme={getAvailabilityColor(listing.availability)}
      borderRadius="full"
      px={2}
      textTransform="capitalize"
    >
      {listing.availability.replaceAll("_", " ")}
    </Badge>

    <Badge
      colorScheme={getVerificationColor(listing.verificationStatus)}
      borderRadius="full"
      px={2}
      textTransform="capitalize"
    >
      {listing.verificationStatus.replaceAll("_", " ")}
    </Badge>
  </HStack>
);

const MarketplaceSyncPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const deleteDisclosure = useDisclosure();
  const detailsDisclosure = useDisclosure();
  const editDisclosure = useDisclosure();

  const [queriesText, setQueriesText] = useState(DEFAULT_QUERIES);
  const [sourceKey, setSourceKey] =
    useState<MarketplaceDiscoverySource>("openai_web");
  const [limit, setLimit] = useState(4);
  const [markupPercent, setMarkupPercent] = useState(15);
  const [publish, setPublish] = useState(true);
  const [generateImages, setGenerateImages] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<MarketplaceSourceFilter>("");
  const [statusFilter, setStatusFilter] = useState<
    "" | MarketplaceVerificationStatus
  >("");
  const [includeIncomplete, setIncludeIncomplete] = useState(true);
  const [loading, setLoading] = useState(false);
  const [runningSync, setRunningSync] = useState(false);
  const [runningReconcile, setRunningReconcile] = useState(false);
  const [runningBulkAction, setRunningBulkAction] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editImageFiles, setEditImageFiles] = useState<SelectedEditImage[]>([]);
  const editImageFilesRef = useRef<SelectedEditImage[]>([]);
  const [sources, setSources] = useState<MarketplaceSourceStatus[]>([]);
  const [runs, setRuns] = useState<MarketplaceRun[]>([]);
  const [listings, setListings] = useState<MarketplaceListing[]>([]);
  const [selectedListingIds, setSelectedListingIds] = useState<string[]>([]);
  const [selectedListing, setSelectedListing] =
    useState<MarketplaceListing | null>(null);
  const [editingListing, setEditingListing] =
    useState<MarketplaceListing | null>(null);
  const [editForm, setEditForm] = useState<ProductEditForm>(EMPTY_EDIT_FORM);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [totalListings, setTotalListings] = useState(0);
  const [syncResult, setSyncResult] = useState<MarketplaceSyncResult | null>(
    null,
  );
  const [reconcileResult, setReconcileResult] =
    useState<MarketplaceReconcileResult | null>(null);
  const [activeRunId, setActiveRunId] = useState("");
  const [liveRun, setLiveRun] = useState<MarketplaceRun | null>(null);

  const queries = useMemo(
    () =>
      queriesText
        .split("\n")
        .map((query) => query.trim())
        .filter(Boolean),
    [queriesText],
  );

  const selectedSourceStatus = useMemo(
    () => sources.find((source) => source.sourceKey === sourceKey),
    [sourceKey, sources],
  );

  const selectedExternalListingIds = useMemo(
    () =>
      listings
        .filter((listing) => selectedListingIds.includes(listing._id))
        .map((listing) => listing._id),
    [listings, selectedListingIds],
  );

  const selectedPublishedListingIds = useMemo(
    () =>
      listings
        .filter(
          (listing) =>
            selectedListingIds.includes(listing._id) &&
            Boolean(listing.entityId),
        )
        .map((listing) => listing._id),
    [listings, selectedListingIds],
  );

  const allListingsSelected =
    listings.length > 0 &&
    listings.every((listing) => selectedListingIds.includes(listing._id));

  const someListingsSelected =
    selectedListingIds.length > 0 && !allListingsSelected;

  const selectedImages = useMemo(
    () => getListingImages(selectedListing),
    [selectedListing],
  );

  const existingEditImages = useMemo(
    () => getListingImages(editingListing),
    [editingListing],
  );

  const runGroups = useMemo(() => groupRunsByPeriod(runs), [runs]);

  const loadData = useCallback(async () => {
    if (!user) {
      return;
    }

    setLoading(true);

    try {
      const verificationStatus: MarketplaceVerificationStatus | undefined =
        statusFilter === "" ? undefined : statusFilter;

      const selectedSource: string | undefined =
        sourceFilter === "" ? undefined : sourceFilter;

      const [statusResult, listingResult] = await Promise.all([
        getMarketplaceStatus(user),
        getMarketplaceListings(user, {
          page: 1,
          limit: 100,
          sourceKey: selectedSource,
          verificationStatus,
          includeIncomplete,
        }),
      ]);

      const returnedSources = statusResult.sources || [];
      const returnedListings = listingResult.items || [];

      setSources(returnedSources);
      setRuns(statusResult.latestRuns || []);
      setListings(returnedListings);
      setTotalListings(listingResult.total || 0);

      setSelectedListingIds((current) =>
        current.filter((listingId) =>
          returnedListings.some((listing) => listing._id === listingId),
        ),
      );

      setSelectedListing((current) => {
        if (!current) {
          return null;
        }

        return (
          returnedListings.find((listing) => listing._id === current._id) ||
          current
        );
      });

      const currentSource = returnedSources.find(
        (source) => source.sourceKey === sourceKey,
      );

      if (!currentSource?.enabled) {
        const openAiSource = returnedSources.find(
          (source) => source.sourceKey === "openai_web" && source.enabled,
        );

        const firstEnabled =
          openAiSource ||
          returnedSources.find(
            (source) =>
              source.enabled &&
              (source.sourceKey === "mock_public" ||
                source.sourceKey === "openai_web"),
          );

        if (firstEnabled) {
          setSourceKey(firstEnabled.sourceKey as MarketplaceDiscoverySource);
        }
      }
    } catch (error: unknown) {
      toast({
        title: "Failed to load marketplace data",
        description: getErrorMessage(error, "Please check the backend."),
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [includeIncomplete, sourceFilter, sourceKey, statusFilter, toast, user]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (!user || !activeRunId) {
      return;
    }

    let cancelled = false;

    const pollRun = async () => {
      try {
        const run = await getMarketplaceRun(user, activeRunId);

        if (cancelled) {
          return;
        }

        setLiveRun(run);

        if (run.status === "completed" || run.status === "failed") {
          setActiveRunId("");
          setRunningSync(false);
          await loadData();
        }
      } catch (error) {
        console.error("Marketplace progress polling failed:", error);
      }
    };

    void pollRun();

    const interval = window.setInterval(() => {
      void pollRun();
    }, 1500);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [activeRunId, loadData, user]);

  useEffect(() => {
    if (detailsDisclosure.isOpen) {
      setActiveImageIndex(0);
    }
  }, [detailsDisclosure.isOpen, selectedListing?._id]);

  useEffect(() => {
    editImageFilesRef.current = editImageFiles;
  }, [editImageFiles]);

  useEffect(
    () => () => {
      editImageFilesRef.current.forEach((image) => {
        URL.revokeObjectURL(image.previewUrl);
      });
    },
    [],
  );

  const handleSync = async () => {
    if (!user) {
      return;
    }

    if (queries.length === 0) {
      toast({
        title: "Enter at least one search query",
        status: "warning",
      });
      return;
    }

    if (!selectedSourceStatus) {
      toast({
        title: "Marketplace source unavailable",
        status: "error",
      });
      return;
    }

    if (!selectedSourceStatus.enabled) {
      toast({
        title: "Marketplace source disabled",
        description: `${sourceKey} is disabled on the backend.`,
        status: "warning",
        duration: 6000,
        isClosable: true,
      });
      return;
    }

    setRunningSync(true);
    setSyncResult(null);
    setLiveRun({
      _id: "",
      status: "running",
      pipelineStage: "discovering",
      progressCurrent: 0,
      progressTotal: limit,
      progressMessage:
        "Searching enabled marketplaces for live product listings…",
      discovered: 0,
      created: 0,
      updated: 0,
      skipped: 0,
      failed: 0,
      errors: [],
    });

    try {
      const result = await searchAndSyncMarketplace(user, {
        entityType: "product",
        queries,
        sourceKeys: [sourceKey],
        limit,
        publish,
        markupPercent,
        generateImages,
        country: "ZA",
        language: "en",
      });

      setSyncResult(result);

      if (result.runId) {
        setActiveRunId(result.runId);
      } else {
        setRunningSync(false);
        await loadData();
      }

      toast({
        title: "Marketplace sync completed",
        description:
          `Discovered ${result.discovered}, ` +
          `created ${result.created}, ` +
          `updated ${result.updated}, ` +
          `skipped ${result.skipped}, ` +
          `failed ${result.failed}.`,
        status: result.failed > 0 ? "warning" : "success",
        duration: 8000,
        isClosable: true,
      });

      if (result.errors?.length) {
        console.warn("Marketplace sync errors:", result.errors);
      }
    } catch (error: unknown) {
      setRunningSync(false);
      setActiveRunId("");

      toast({
        title: "Marketplace sync failed",
        description: getErrorMessage(error, "Unknown sync error."),
        status: "error",
        duration: 8000,
        isClosable: true,
      });
    }
  };

  const handleReconcile = async () => {
    if (!user) {
      return;
    }

    setRunningReconcile(true);
    setReconcileResult(null);

    try {
      const result = await reconcileMarketplace(user, "product");

      setReconcileResult(result);

      toast({
        title: "Reconciliation completed",
        description:
          `Checked ${result.checked}, ` +
          `updated ${result.updated}, ` +
          `unavailable ${result.unavailable}, ` +
          `archived ${result.archived}, ` +
          `failed ${result.failed}.`,
        status: result.failed > 0 ? "warning" : "success",
        duration: 8000,
        isClosable: true,
      });

      await loadData();
    } catch (error: unknown) {
      toast({
        title: "Reconciliation failed",
        description: getErrorMessage(error, "Unknown reconciliation error."),
        status: "error",
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setRunningReconcile(false);
    }
  };

  const handleBulkUpdate = async (update: Record<string, unknown>) => {
    if (!user || selectedPublishedListingIds.length === 0) {
      return;
    }

    setRunningBulkAction(true);

    try {
      const result = await bulkUpdateMarketplaceProducts(
        user,
        selectedPublishedListingIds,
        update,
      );

      const updated = getUpdatedCount(result);
      const failed = Number((result as { failed?: number })?.failed || 0);

      toast({
        title: "Products updated",
        description: `${updated} updated, ${failed} failed.`,
        status: failed > 0 ? "warning" : "success",
        duration: 7000,
        isClosable: true,
      });

      await loadData();
    } catch (error: unknown) {
      toast({
        title: "Bulk update failed",
        description: getErrorMessage(
          error,
          "Could not update selected products.",
        ),
        status: "error",
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setRunningBulkAction(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!user || selectedExternalListingIds.length === 0) {
      return;
    }

    setRunningBulkAction(true);

    try {
      const result = await bulkDeleteMarketplaceProducts(
        user,
        selectedExternalListingIds,
      );

      toast({
        title: "Marketplace records deleted",
        description: `${result.deleted} deleted, ${result.failed} failed.`,
        status: result.failed > 0 ? "warning" : "success",
        duration: 7000,
        isClosable: true,
      });

      setSelectedListingIds([]);
      deleteDisclosure.onClose();
      await loadData();
    } catch (error: unknown) {
      toast({
        title: "Delete failed",
        description: getErrorMessage(
          error,
          "Could not delete selected records.",
        ),
        status: "error",
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setRunningBulkAction(false);
    }
  };

  const clearEditImageFiles = () => {
    setEditImageFiles((current) => {
      current.forEach((image) => {
        URL.revokeObjectURL(image.previewUrl);
      });

      return [];
    });
  };

  const handleEditImageSelection = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";

    if (!files.length) {
      return;
    }

    const acceptedFiles: File[] = [];

    files.forEach((file) => {
      if (!ACCEPTED_EDIT_IMAGE_TYPES.has(file.type)) {
        toast({
          title: "Unsupported image",
          description: `${file.name} must be JPG, PNG, WebP or AVIF.`,
          status: "warning",
          duration: 5000,
          isClosable: true,
        });

        return;
      }

      if (file.size > MAX_EDIT_IMAGE_BYTES) {
        toast({
          title: "Image too large",
          description: `${file.name} is larger than 12 MB.`,
          status: "warning",
          duration: 5000,
          isClosable: true,
        });

        return;
      }

      acceptedFiles.push(file);
    });

    setEditImageFiles((current) => {
      const existingIds = new Set(current.map((image) => image.id));

      const additions = acceptedFiles
        .filter((file) => !existingIds.has(getSelectedImageId(file)))
        .map((file) => ({
          id: getSelectedImageId(file),
          file,
          previewUrl: URL.createObjectURL(file),
        }));

      const next = [...current, ...additions];

      if (next.length > MAX_EDIT_IMAGES) {
        next.slice(MAX_EDIT_IMAGES).forEach((image) => {
          URL.revokeObjectURL(image.previewUrl);
        });

        toast({
          title: "Too many images",
          description: `A product can have at most ${MAX_EDIT_IMAGES} images.`,
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      }

      return next.slice(0, MAX_EDIT_IMAGES);
    });
  };

  const removeEditImage = (imageId: string) => {
    setEditImageFiles((current) => {
      const removedImage = current.find((image) => image.id === imageId);

      if (removedImage) {
        URL.revokeObjectURL(removedImage.previewUrl);
      }

      return current.filter((image) => image.id !== imageId);
    });
  };

  const makeEditImageMain = (imageId: string) => {
    setEditImageFiles((current) => {
      const selectedImage = current.find((image) => image.id === imageId);

      if (!selectedImage) {
        return current;
      }

      return [
        selectedImage,
        ...current.filter((image) => image.id !== imageId),
      ];
    });
  };

  const handleSaveEdit = async () => {
    if (!user || !editingListing) {
      return;
    }

    if (!editingListing.entityId) {
      toast({
        title: "Product has not been published",
        description: "Publish this marketplace listing before editing it.",
        status: "warning",
      });
      return;
    }

    const title = editForm.title.trim();
    const category = editForm.category.trim();

    if (!title) {
      toast({
        title: "Product title is required",
        status: "warning",
      });
      return;
    }

    if (!category) {
      toast({
        title: "Product category is required",
        status: "warning",
      });
      return;
    }

    if (!Number.isFinite(editForm.price) || editForm.price <= 0) {
      toast({
        title: "Enter a valid product price",
        status: "warning",
      });
      return;
    }

    if (!Number.isFinite(editForm.stockQty) || editForm.stockQty < 0) {
      toast({
        title: "Enter a valid stock quantity",
        status: "warning",
      });
      return;
    }

    setSavingEdit(true);

    try {
      const result = await editMarketplaceProduct(
        user,
        editingListing._id,
        {
          title,
          description: editForm.description.trim(),
          brand: editForm.brand.trim(),
          category,
          price: Math.round(editForm.price),
          stockQty: Math.floor(editForm.stockQty),
          status: editForm.status,
          isFeatured: editForm.isFeatured,
        },
        editImageFiles.map((image) => image.file),
        0,
      );

      toast({
        title: "Product updated",
        description:
          result.uploadedImages > 0
            ? `${result.uploadedImages} image(s) uploaded. The first image is now the main product image.`
            : "Product details saved. Existing images were kept.",
        status: "success",
        duration: 6000,
        isClosable: true,
      });

      clearEditImageFiles();
      editDisclosure.onClose();
      setEditingListing(null);
      setEditForm(EMPTY_EDIT_FORM);
      await loadData();
    } catch (error: unknown) {
      toast({
        title: "Product update failed",
        description: getErrorMessage(error, "Could not update the product."),
        status: "error",
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setSavingEdit(false);
    }
  };

  const toggleListing = (listingId: string, checked: boolean) => {
    setSelectedListingIds((current) => {
      if (checked) {
        return current.includes(listingId) ? current : [...current, listingId];
      }

      return current.filter((currentId) => currentId !== listingId);
    });
  };

  const toggleAllListings = (checked: boolean) => {
    setSelectedListingIds(
      checked ? listings.map((listing) => listing._id) : [],
    );
  };

  const openDetails = (listing: MarketplaceListing) => {
    setSelectedListing(listing);
    setActiveImageIndex(0);
    detailsDisclosure.onOpen();
  };

  const closeDetails = () => {
    detailsDisclosure.onClose();
    setSelectedListing(null);
    setActiveImageIndex(0);
  };

  const openEdit = (listing: MarketplaceListing) => {
    if (!listing.entityId || !listing.product) {
      toast({
        title: "Product has not been published",
        description: "Only published marketplace products can be edited.",
        status: "warning",
      });
      return;
    }

    clearEditImageFiles();
    setEditingListing(listing);
    setEditForm({
      title: listing.product.title || listing.originalTitle || "",
      description:
        listing.product.description || listing.originalDescription || "",
      brand: listing.product.brand || "",
      category: listing.product.category || "Imported Products",
      price: Number(listing.product.price || 0),
      stockQty: Number(listing.product.stockQty ?? listing.stockQty ?? 0),
      status: (listing.product.status || "active") as ProductStatus,
      isFeatured: Boolean(listing.product.isFeatured),
    });

    detailsDisclosure.onClose();
    editDisclosure.onOpen();
  };

  const closeEdit = () => {
    if (savingEdit) {
      return;
    }

    clearEditImageFiles();
    editDisclosure.onClose();
    setEditingListing(null);
    setEditForm(EMPTY_EDIT_FORM);
  };

  const previousImage = () => {
    setActiveImageIndex((current) =>
      selectedImages.length
        ? (current - 1 + selectedImages.length) % selectedImages.length
        : 0,
    );
  };

  const nextImage = () => {
    setActiveImageIndex((current) =>
      selectedImages.length ? (current + 1) % selectedImages.length : 0,
    );
  };

  if (!user) {
    return (
      <Box p={6}>
        <Alert status="warning" borderRadius="xl">
          <AlertIcon />
          <Box>
            <AlertTitle>Sign in required</AlertTitle>
            <AlertDescription>
              Sign in before using the marketplace sync page.
            </AlertDescription>
            <Button mt={4} onClick={() => navigate("/login")}>
              Go to login
            </Button>
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      minH="100vh"
      p={{ base: 3, sm: 4, md: 6 }}
      bg="gray.50"
      _dark={{ bg: "gray.900" }}
    >
      <VStack maxW="1500px" mx="auto" align="stretch" spacing={6}>
        <Flex
          justify="space-between"
          align={{ base: "stretch", md: "center" }}
          direction={{ base: "column", md: "row" }}
          gap={4}
        >
          <Box>
            <Heading size={{ base: "md", md: "lg" }}>
              Marketplace products
            </Heading>
            <Text mt={1.5} color="gray.500" fontSize={{ base: "sm", md: "md" }}>
              Discover, publish and manage public marketplace products.
            </Text>
          </Box>

          <Button
            leftIcon={<RefreshCcw size={16} />}
            onClick={() => void loadData()}
            isLoading={loading}
            alignSelf={{ base: "stretch", md: "auto" }}
          >
            Refresh
          </Button>
        </Flex>

        <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={5}>
          <Box
            bg="white"
            _dark={{ bg: "gray.800" }}
            borderWidth="1px"
            borderRadius="2xl"
            p={{ base: 4, md: 5 }}
          >
            <Heading size="md" mb={5}>
              Search and sync
            </Heading>

            <VStack align="stretch" spacing={4}>
              <FormControl>
                <FormLabel>Marketplace source</FormLabel>
                <Select
                  value={sourceKey}
                  onChange={(event) =>
                    setSourceKey(
                      event.target.value as MarketplaceDiscoverySource,
                    )
                  }
                >
                  <option value="openai_web">OpenAI web search</option>
                  <option value="mock_public">Mock public</option>
                </Select>

                <Text
                  mt={1.5}
                  fontSize="xs"
                  color={
                    selectedSourceStatus?.enabled ? "green.500" : "orange.500"
                  }
                >
                  {selectedSourceStatus
                    ? selectedSourceStatus.enabled
                      ? "Ready to search."
                      : "Disabled on the backend."
                    : "Checking source status…"}
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel>Product searches</FormLabel>
                <Textarea
                  value={queriesText}
                  onChange={(event) => setQueriesText(event.target.value)}
                  rows={5}
                  resize="vertical"
                  placeholder="One search term per line"
                />
                <Text mt={1.5} fontSize="xs" color="gray.500">
                  {queries.length} search term(s). Use one query per line.
                </Text>
              </FormControl>

              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>Result limit</FormLabel>
                  <NumberInput
                    value={limit}
                    min={1}
                    max={100}
                    onChange={(_value, numberValue) =>
                      setLimit(Number.isFinite(numberValue) ? numberValue : 1)
                    }
                  >
                    <NumberInputField />
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>Markup %</FormLabel>
                  <NumberInput
                    value={markupPercent}
                    min={0}
                    max={500}
                    onChange={(_value, numberValue) =>
                      setMarkupPercent(
                        Number.isFinite(numberValue) ? numberValue : 0,
                      )
                    }
                  >
                    <NumberInputField />
                  </NumberInput>
                </FormControl>
              </SimpleGrid>

              <Stack spacing={3}>
                <Checkbox
                  isChecked={publish}
                  onChange={(event) => setPublish(event.target.checked)}
                >
                  Publish valid results as products
                </Checkbox>

                <Checkbox
                  isChecked={generateImages}
                  onChange={(event) => setGenerateImages(event.target.checked)}
                >
                  Generate and store two Firebase images
                </Checkbox>
              </Stack>

              <Stack direction={{ base: "column", sm: "row" }} spacing={3}>
                <Button
                  leftIcon={<Play size={16} />}
                  colorScheme="teal"
                  onClick={() => void handleSync()}
                  isLoading={runningSync}
                  isDisabled={!selectedSourceStatus?.enabled}
                  loadingText="Running"
                  flex={1}
                >
                  Run sync
                </Button>

                <Button
                  leftIcon={<RefreshCcw size={16} />}
                  onClick={() => void handleReconcile()}
                  isLoading={runningReconcile}
                  flex={1}
                >
                  Reconcile
                </Button>
              </Stack>
            </VStack>
          </Box>

          <Box
            bg="white"
            _dark={{ bg: "gray.800" }}
            borderWidth="1px"
            borderRadius="2xl"
            p={{ base: 4, md: 5 }}
          >
            <Flex justify="space-between" align="center" mb={4}>
              <Heading size="md">Overview</Heading>
              <Badge borderRadius="full" px={2.5} py={1}>
                {totalListings} listings
              </Badge>
            </Flex>

            <Text fontSize="sm" color="gray.500" mb={3}>
              Discovery sources
            </Text>

            <HStack spacing={2} flexWrap="wrap">
              {sources.map((source) => (
                <Badge
                  key={source.sourceKey}
                  colorScheme={source.enabled ? "green" : "gray"}
                  borderRadius="full"
                  px={3}
                  py={1.5}
                  textTransform="none"
                >
                  {source.sourceKey === "openai_web"
                    ? "OpenAI web"
                    : source.sourceKey.replaceAll("_", " ")}
                  {source.enabled ? " · enabled" : " · disabled"}
                </Badge>
              ))}
            </HStack>

            {(syncResult || reconcileResult) && <Divider my={5} />}

            {syncResult && (
              <Box>
                <Text fontSize="sm" fontWeight="semibold" mb={3}>
                  Latest sync
                </Text>
                <SimpleGrid columns={{ base: 2, sm: 3, md: 5 }} spacing={2}>
                  <ResultStat label="Found" value={syncResult.discovered} />
                  <ResultStat label="Created" value={syncResult.created} />
                  <ResultStat label="Updated" value={syncResult.updated} />
                  <ResultStat label="Skipped" value={syncResult.skipped} />
                  <ResultStat label="Failed" value={syncResult.failed} />
                </SimpleGrid>
              </Box>
            )}

            {reconcileResult && (
              <Box mt={syncResult ? 5 : 0}>
                <Text fontSize="sm" fontWeight="semibold" mb={3}>
                  Latest reconciliation
                </Text>
                <SimpleGrid columns={{ base: 2, sm: 3, md: 5 }} spacing={2}>
                  <ResultStat label="Checked" value={reconcileResult.checked} />
                  <ResultStat label="Updated" value={reconcileResult.updated} />
                  <ResultStat
                    label="Unavailable"
                    value={reconcileResult.unavailable}
                  />
                  <ResultStat
                    label="Archived"
                    value={reconcileResult.archived}
                  />
                  <ResultStat label="Failed" value={reconcileResult.failed} />
                </SimpleGrid>
              </Box>
            )}

            {!syncResult && !reconcileResult && (
              <Flex
                minH="170px"
                align="center"
                justify="center"
                textAlign="center"
              >
                <Text color="gray.500" maxW="360px">
                  Run a marketplace search or reconciliation to see a compact
                  result summary here.
                </Text>
              </Flex>
            )}
          </Box>
        </SimpleGrid>

        <Box
          bg="white"
          _dark={{ bg: "gray.800" }}
          borderWidth="1px"
          borderRadius="2xl"
          p={{ base: 4, md: 5 }}
        >
          <Flex
            justify="space-between"
            align={{ base: "stretch", xl: "center" }}
            direction={{ base: "column", xl: "row" }}
            gap={4}
            mb={5}
          >
            <Box>
              <Heading size="md">Products</Heading>
              <Text mt={1} color="gray.500" fontSize="sm">
                Showing {listings.length} of {totalListings} records.
              </Text>
            </Box>

            <SimpleGrid
              columns={{ base: 1, sm: 2, lg: 4 }}
              spacing={2}
              w={{ base: "100%", xl: "auto" }}
            >
              <Select
                value={sourceFilter}
                onChange={(event) =>
                  setSourceFilter(event.target.value as MarketplaceSourceFilter)
                }
                minW={{ xl: "160px" }}
              >
                <option value="">All sources</option>
                <option value="openai_web">OpenAI web</option>
                <option value="mock_public">Mock public</option>
                <option value="public_page">Public page</option>
              </Select>

              <Select
                value={statusFilter}
                onChange={(event) =>
                  setStatusFilter(
                    event.target.value as "" | MarketplaceVerificationStatus,
                  )
                }
                minW={{ xl: "160px" }}
              >
                <option value="">All statuses</option>
                <option value="discovered">Discovered</option>
                <option value="verified">Verified</option>
                <option value="stale">Stale</option>
                <option value="unavailable">Unavailable</option>
                <option value="blocked">Blocked</option>
              </Select>

              <Flex
                align="center"
                minH="40px"
                px={3}
                borderWidth="1px"
                borderRadius="md"
              >
                <Checkbox
                  isChecked={includeIncomplete}
                  onChange={(event) =>
                    setIncludeIncomplete(event.target.checked)
                  }
                >
                  Incomplete
                </Checkbox>
              </Flex>

              <Button
                leftIcon={<Search size={16} />}
                onClick={() => void loadData()}
              >
                Apply
              </Button>
            </SimpleGrid>
          </Flex>

          {selectedListingIds.length > 0 && (
            <Box
              mb={4}
              borderWidth="1px"
              borderRadius="xl"
              p={3}
              bg="gray.50"
              _dark={{ bg: "whiteAlpha.50" }}
            >
              <Flex
                direction={{ base: "column", lg: "row" }}
                justify="space-between"
                align={{ base: "stretch", lg: "center" }}
                gap={3}
              >
                <Text fontSize="sm">
                  <Text as="span" fontWeight="bold">
                    {selectedExternalListingIds.length}
                  </Text>{" "}
                  selected · {selectedPublishedListingIds.length} published
                </Text>

                <HStack spacing={2} flexWrap="wrap">
                  <Button
                    size="sm"
                    onClick={() => void handleBulkUpdate({ status: "active" })}
                    isDisabled={
                      selectedPublishedListingIds.length === 0 ||
                      runningBulkAction
                    }
                  >
                    Activate
                  </Button>

                  <Button
                    size="sm"
                    onClick={() =>
                      void handleBulkUpdate({
                        status: "out_of_stock",
                        stockQty: 0,
                      })
                    }
                    isDisabled={
                      selectedPublishedListingIds.length === 0 ||
                      runningBulkAction
                    }
                  >
                    Out of stock
                  </Button>

                  <Button
                    size="sm"
                    onClick={() => void handleBulkUpdate({ isFeatured: true })}
                    isDisabled={
                      selectedPublishedListingIds.length === 0 ||
                      runningBulkAction
                    }
                  >
                    Feature
                  </Button>

                  <Button
                    size="sm"
                    colorScheme="red"
                    variant="outline"
                    leftIcon={<Trash2 size={15} />}
                    onClick={deleteDisclosure.onOpen}
                    isDisabled={runningBulkAction}
                  >
                    Delete
                  </Button>
                </HStack>
              </Flex>

              {deleteDisclosure.isOpen && (
                <Alert mt={3} status="error" borderRadius="lg">
                  <AlertIcon />
                  <Box flex="1">
                    <AlertTitle>Delete selected records?</AlertTitle>
                    <AlertDescription fontSize="sm">
                      Published products, marketplace records, catalog entries
                      and generated Firebase images will be removed.
                    </AlertDescription>
                    <HStack mt={3}>
                      <Button
                        size="sm"
                        colorScheme="red"
                        onClick={() => void handleBulkDelete()}
                        isLoading={runningBulkAction}
                      >
                        Confirm delete
                      </Button>
                      <Button size="sm" onClick={deleteDisclosure.onClose}>
                        Cancel
                      </Button>
                    </HStack>
                  </Box>
                </Alert>
              )}
            </Box>
          )}

          {loading ? (
            <Skeleton h="320px" borderRadius="xl" />
          ) : (
            <>
              <Box display={{ base: "none", lg: "block" }}>
                <TableContainer>
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th w="48px">
                          <Checkbox
                            isChecked={allListingsSelected}
                            isIndeterminate={someListingsSelected}
                            onChange={(event) =>
                              toggleAllListings(event.target.checked)
                            }
                          />
                        </Th>
                        <Th>Product</Th>
                        <Th>Price</Th>
                        <Th>Source</Th>
                        <Th>Status</Th>
                        <Th>Updated</Th>
                        <Th textAlign="right">Edit</Th>
                      </Tr>
                    </Thead>

                    <Tbody>
                      {listings.map((listing) => {
                        const price = getListingPrice(listing);

                        return (
                          <Tr
                            key={listing._id}
                            _hover={{ bg: "blackAlpha.50" }}
                          >
                            <Td>
                              <Checkbox
                                isChecked={selectedListingIds.includes(
                                  listing._id,
                                )}
                                onChange={(event) =>
                                  toggleListing(
                                    listing._id,
                                    event.target.checked,
                                  )
                                }
                              />
                            </Td>

                            <Td maxW="330px">
                              <HStack spacing={3} align="center">
                                <ProductImageButton
                                  listing={listing}
                                  onOpen={() => openDetails(listing)}
                                />

                                <Box minW={0} maxW="235px">
                                  <Text fontWeight="semibold" noOfLines={2}>
                                    {getListingTitle(listing)}
                                  </Text>
                                  <Text
                                    mt={1}
                                    fontSize="xs"
                                    color="gray.500"
                                    noOfLines={1}
                                  >
                                    {getListingDescription(listing)}
                                  </Text>
                                  <HStack mt={1.5} spacing={1.5}>
                                    <Badge
                                      borderRadius="full"
                                      colorScheme={
                                        listing.entityId ? "green" : "gray"
                                      }
                                      fontSize="9px"
                                    >
                                      {listing.entityId
                                        ? "Published"
                                        : "Not published"}
                                    </Badge>
                                    <Text fontSize="10px" color="gray.400">
                                      {getListingImages(listing).length}{" "}
                                      image(s)
                                    </Text>
                                  </HStack>
                                </Box>
                              </HStack>
                            </Td>

                            <Td whiteSpace="nowrap">
                              <Text fontWeight="bold">
                                {formatMoney(price.value, price.currency)}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                {price.label}
                              </Text>
                            </Td>

                            <Td maxW="170px">
                              <Text fontWeight="medium" noOfLines={1}>
                                {listing.retailer || "Unknown retailer"}
                              </Text>
                              <HStack mt={1} spacing={1.5}>
                                <Code fontSize="10px" noOfLines={1}>
                                  {listing.sourceKey}
                                </Code>
                                <Tooltip label="Open source listing">
                                  <IconButton
                                    as={Link}
                                    href={listing.sourceUrl}
                                    isExternal
                                    aria-label="Open source listing"
                                    icon={<ExternalLink size={13} />}
                                    size="xs"
                                    variant="ghost"
                                  />
                                </Tooltip>
                              </HStack>
                            </Td>

                            <Td>
                              <ListingBadges listing={listing} />
                            </Td>

                            <Td whiteSpace="nowrap">
                              <Text fontSize="xs">
                                {formatDate(listing.updatedAt)}
                              </Text>
                            </Td>

                            <Td textAlign="right">
                              <Tooltip
                                label={
                                  listing.entityId
                                    ? "Edit product"
                                    : "Publish before editing"
                                }
                              >
                                <IconButton
                                  aria-label="Edit product"
                                  icon={<Edit3 size={15} />}
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openEdit(listing)}
                                  isDisabled={!listing.entityId}
                                />
                              </Tooltip>
                            </Td>
                          </Tr>
                        );
                      })}

                      {listings.length === 0 && (
                        <Tr>
                          <Td colSpan={7} py={12} textAlign="center">
                            <Text color="gray.500">
                              No marketplace records found.
                            </Text>
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>

              <VStack
                display={{ base: "flex", lg: "none" }}
                align="stretch"
                spacing={3}
              >
                <Flex align="center" justify="space-between" px={1}>
                  <Checkbox
                    isChecked={allListingsSelected}
                    isIndeterminate={someListingsSelected}
                    onChange={(event) =>
                      toggleAllListings(event.target.checked)
                    }
                  >
                    Select all
                  </Checkbox>
                  <Text fontSize="xs" color="gray.500">
                    {listings.length} product(s)
                  </Text>
                </Flex>

                {listings.map((listing) => {
                  const price = getListingPrice(listing);

                  return (
                    <Box
                      key={listing._id}
                      borderWidth="1px"
                      borderRadius="xl"
                      p={3}
                    >
                      <Flex justify="space-between" align="flex-start" gap={3}>
                        <Checkbox
                          mt={1}
                          isChecked={selectedListingIds.includes(listing._id)}
                          onChange={(event) =>
                            toggleListing(listing._id, event.target.checked)
                          }
                        />

                        <HStack
                          align="flex-start"
                          spacing={3}
                          flex={1}
                          minW={0}
                        >
                          <ProductImageButton
                            listing={listing}
                            size={{ base: "86px", sm: "96px" }}
                            onOpen={() => openDetails(listing)}
                          />

                          <Box minW={0} flex={1}>
                            <Text fontWeight="semibold" noOfLines={2}>
                              {getListingTitle(listing)}
                            </Text>
                            <Text
                              mt={1}
                              fontSize="xs"
                              color="gray.500"
                              noOfLines={2}
                            >
                              {getListingDescription(listing)}
                            </Text>
                            <Text mt={2} fontWeight="bold">
                              {formatMoney(price.value, price.currency)}
                            </Text>
                            <Text fontSize="10px" color="gray.500">
                              {price.label}
                            </Text>
                          </Box>
                        </HStack>
                      </Flex>

                      <Divider my={3} />

                      <Flex
                        justify="space-between"
                        align={{ base: "stretch", sm: "center" }}
                        direction={{ base: "column", sm: "row" }}
                        gap={3}
                      >
                        <Box>
                          <ListingBadges listing={listing} />
                          <Text mt={1.5} fontSize="xs" color="gray.500">
                            {listing.retailer || "Unknown retailer"} ·{" "}
                            {formatDate(listing.updatedAt)}
                          </Text>
                        </Box>

                        <HStack>
                          <Button
                            as={Link}
                            href={listing.sourceUrl}
                            isExternal
                            size="sm"
                            variant="ghost"
                            rightIcon={<ExternalLink size={13} />}
                          >
                            Source
                          </Button>

                          <Button
                            size="sm"
                            leftIcon={<Edit3 size={14} />}
                            onClick={() => openEdit(listing)}
                            isDisabled={!listing.entityId}
                          >
                            Edit
                          </Button>
                        </HStack>
                      </Flex>
                    </Box>
                  );
                })}

                {listings.length === 0 && (
                  <Box py={10} textAlign="center">
                    <Text color="gray.500">No marketplace records found.</Text>
                  </Box>
                )}
              </VStack>
            </>
          )}
        </Box>

        <Box
          bg="white"
          _dark={{ bg: "gray.800" }}
          borderWidth="1px"
          borderRadius="2xl"
          p={{ base: 4, md: 5 }}
        >
          <Flex justify="space-between" align="center" mb={3}>
            <Box>
              <Heading size="md">Run history</Heading>
              <Text mt={1} color="gray.500" fontSize="sm">
                Recent marketplace activity grouped by period.
              </Text>
            </Box>
            <Badge borderRadius="full" px={2.5} py={1}>
              {runs.length}
            </Badge>
          </Flex>

          {runGroups.length > 0 ? (
            <Accordion allowMultiple defaultIndex={[0]}>
              {runGroups.map((group) => (
                <AccordionItem key={group.key} border="0">
                  <AccordionButton px={0} py={3} _hover={{ bg: "transparent" }}>
                    <Box flex="1" textAlign="left">
                      <HStack>
                        <Text fontWeight="semibold">{group.label}</Text>
                        <Badge borderRadius="full">{group.runs.length}</Badge>
                      </HStack>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>

                  <AccordionPanel px={0} pt={0} pb={4}>
                    <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={3}>
                      {group.runs.map((run) => (
                        <RunHistoryCard key={run._id} run={run} />
                      ))}
                    </SimpleGrid>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <Text color="gray.500" py={6}>
              No marketplace runs yet.
            </Text>
          )}
        </Box>
      </VStack>

      <Modal
        isOpen={detailsDisclosure.isOpen}
        onClose={closeDetails}
        size="6xl"
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay backdropFilter="blur(6px)" />
        <ModalContent borderRadius="2xl" overflow="hidden" mx={3}>
          <ModalHeader pr={12} noOfLines={1}>
            {selectedListing
              ? getListingTitle(selectedListing)
              : "Marketplace product"}
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody pb={6}>
            {selectedListing && (
              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                <VStack align="stretch" spacing={3}>
                  <Box
                    position="relative"
                    borderWidth="1px"
                    borderRadius="xl"
                    overflow="hidden"
                    h={{ base: "280px", md: "430px" }}
                    bg="gray.50"
                  >
                    <Image
                      src={
                        selectedImages[activeImageIndex] ||
                        FALLBACK_PRODUCT_IMAGE
                      }
                      fallbackSrc={FALLBACK_PRODUCT_IMAGE}
                      alt={getListingTitle(selectedListing)}
                      w="100%"
                      h="100%"
                      objectFit="contain"
                    />

                    {selectedImages.length > 1 && (
                      <>
                        <IconButton
                          aria-label="Previous image"
                          icon={<ChevronLeft size={20} />}
                          position="absolute"
                          left={3}
                          top="50%"
                          transform="translateY(-50%)"
                          borderRadius="full"
                          bg="whiteAlpha.800"
                          backdropFilter="blur(8px)"
                          onClick={previousImage}
                        />
                        <IconButton
                          aria-label="Next image"
                          icon={<ChevronRight size={20} />}
                          position="absolute"
                          right={3}
                          top="50%"
                          transform="translateY(-50%)"
                          borderRadius="full"
                          bg="whiteAlpha.800"
                          backdropFilter="blur(8px)"
                          onClick={nextImage}
                        />
                      </>
                    )}
                  </Box>

                  {selectedImages.length > 0 && (
                    <HStack spacing={2} overflowX="auto" py={1}>
                      {selectedImages.map((imageUrl, index) => (
                        <Box
                          as="button"
                          type="button"
                          key={`${imageUrl}-${index}`}
                          flex="0 0 auto"
                          borderWidth={
                            index === activeImageIndex ? "2px" : "1px"
                          }
                          borderColor={
                            index === activeImageIndex ? "teal.500" : "gray.200"
                          }
                          borderRadius="lg"
                          overflow="hidden"
                          cursor="pointer"
                          onClick={() => setActiveImageIndex(index)}
                        >
                          <Image
                            src={imageUrl}
                            fallbackSrc={FALLBACK_PRODUCT_IMAGE}
                            boxSize="66px"
                            objectFit="cover"
                          />
                        </Box>
                      ))}
                    </HStack>
                  )}
                </VStack>

                <VStack align="stretch" spacing={4}>
                  <Box>
                    <Heading size="md">
                      {getListingTitle(selectedListing)}
                    </Heading>
                    <HStack mt={3} flexWrap="wrap">
                      <ListingBadges listing={selectedListing} />
                      <Badge
                        colorScheme={
                          selectedListing.entityId ? "green" : "gray"
                        }
                        borderRadius="full"
                        px={2}
                      >
                        {selectedListing.entityId
                          ? "Published"
                          : "Not published"}
                      </Badge>
                    </HStack>
                  </Box>

                  <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                    <Box borderWidth="1px" borderRadius="xl" p={3}>
                      <Text fontSize="xs" color="gray.500">
                        PickDengu price
                      </Text>
                      <Text mt={1} fontSize="xl" fontWeight="bold">
                        {formatMoney(selectedListing.product?.price, "MWK")}
                      </Text>
                    </Box>

                    <Box borderWidth="1px" borderRadius="xl" p={3}>
                      <Text fontSize="xs" color="gray.500">
                        Original source price
                      </Text>
                      <Text mt={1} fontSize="xl" fontWeight="bold">
                        {formatMoney(
                          selectedListing.observedPrice,
                          selectedListing.observedCurrency || "ZAR",
                        )}
                      </Text>
                    </Box>
                  </SimpleGrid>

                  <Box>
                    <Text fontSize="sm" color="gray.500" mb={1}>
                      Description
                    </Text>
                    <Text whiteSpace="pre-wrap">
                      {getListingDescription(selectedListing)}
                    </Text>
                  </Box>

                  <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Retailer
                      </Text>
                      <Text fontWeight="semibold">
                        {selectedListing.retailer || "Unknown retailer"}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Category
                      </Text>
                      <Text fontWeight="semibold">
                        {selectedListing.product?.category || "—"}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Brand
                      </Text>
                      <Text fontWeight="semibold">
                        {selectedListing.product?.brand || "—"}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Stock
                      </Text>
                      <Text fontWeight="semibold">
                        {selectedListing.product?.stockQty ?? "—"}
                      </Text>
                    </Box>
                  </SimpleGrid>

                  <Stack direction={{ base: "column", sm: "row" }} spacing={3}>
                    <Button
                      leftIcon={<Edit3 size={16} />}
                      colorScheme="teal"
                      onClick={() => openEdit(selectedListing)}
                      isDisabled={!selectedListing.entityId}
                      flex={1}
                    >
                      Edit product
                    </Button>

                    <Button
                      as={Link}
                      href={selectedListing.sourceUrl}
                      isExternal
                      rightIcon={<ExternalLink size={14} />}
                      variant="outline"
                      flex={1}
                    >
                      Open source
                    </Button>
                  </Stack>
                </VStack>
              </SimpleGrid>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={editDisclosure.isOpen}
        onClose={closeEdit}
        size="5xl"
        isCentered
        scrollBehavior="inside"
        closeOnOverlayClick={!savingEdit}
        closeOnEsc={!savingEdit}
      >
        <ModalOverlay backdropFilter="blur(6px)" />
        <ModalContent borderRadius="2xl" mx={3}>
          <ModalHeader>Edit PickDengu product</ModalHeader>
          <ModalCloseButton isDisabled={savingEdit} />

          <ModalBody>
            <Alert status="info" borderRadius="xl" mb={5}>
              <AlertIcon />
              <AlertDescription fontSize="sm">
                These changes update the linked PickDengu product. The original
                marketplace title, price and source record remain available for
                verification.
              </AlertDescription>
            </Alert>

            <VStack align="stretch" spacing={4}>
              <Box borderWidth="1px" borderRadius="xl" p={{ base: 3, md: 4 }}>
                <Flex
                  justify="space-between"
                  align={{ base: "stretch", sm: "center" }}
                  direction={{ base: "column", sm: "row" }}
                  gap={3}
                >
                  <Box>
                    <Text fontWeight="semibold">Product images</Text>
                    <Text fontSize="sm" color="gray.500">
                      Upload image files from your device. Do not paste URLs.
                    </Text>
                  </Box>

                  <Button
                    as="label"
                    colorScheme="teal"
                    variant="outline"
                    leftIcon={<Upload size={16} />}
                    cursor="pointer"
                    isDisabled={savingEdit}
                    flexShrink={0}
                  >
                    Upload images
                    <Input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/avif"
                      multiple
                      display="none"
                      onChange={handleEditImageSelection}
                    />
                  </Button>
                </Flex>

                <Alert status="info" borderRadius="lg" mt={4} py={2}>
                  <AlertIcon />
                  <AlertDescription fontSize="sm">
                    Uploading new images replaces the current product image set.
                    The first selected image becomes the main image. Maximum 8
                    files and 12 MB per file.
                  </AlertDescription>
                </Alert>

                {editImageFiles.length > 0 ? (
                  <SimpleGrid
                    columns={{ base: 1, sm: 2, md: 3 }}
                    spacing={3}
                    mt={4}
                  >
                    {editImageFiles.map((image, index) => (
                      <Box
                        key={image.id}
                        borderWidth={index === 0 ? "2px" : "1px"}
                        borderColor={index === 0 ? "teal.400" : "gray.200"}
                        borderRadius="xl"
                        overflow="hidden"
                        bg="gray.50"
                        _dark={{ bg: "whiteAlpha.50" }}
                      >
                        <Image
                          src={image.previewUrl}
                          alt={image.file.name}
                          w="100%"
                          h="150px"
                          objectFit="contain"
                        />

                        <Box p={2.5}>
                          <Text fontSize="xs" noOfLines={1}>
                            {image.file.name}
                          </Text>

                          <HStack mt={2} spacing={2} flexWrap="wrap">
                            {index === 0 ? (
                              <Badge colorScheme="teal">Main image</Badge>
                            ) : (
                              <Button
                                size="xs"
                                variant="outline"
                                leftIcon={<Star size={12} />}
                                onClick={() => makeEditImageMain(image.id)}
                                isDisabled={savingEdit}
                              >
                                Make main
                              </Button>
                            )}

                            <Button
                              size="xs"
                              variant="ghost"
                              colorScheme="red"
                              leftIcon={<Trash2 size={12} />}
                              onClick={() => removeEditImage(image.id)}
                              isDisabled={savingEdit}
                            >
                              Remove
                            </Button>
                          </HStack>
                        </Box>
                      </Box>
                    ))}
                  </SimpleGrid>
                ) : existingEditImages.length > 0 ? (
                  <Box mt={4}>
                    <HStack mb={3}>
                      <Badge>{existingEditImages.length} current</Badge>
                      <Text fontSize="xs" color="gray.500">
                        These stay unchanged until new files are uploaded.
                      </Text>
                    </HStack>

                    <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} spacing={3}>
                      {existingEditImages.map((imageUrl, index) => (
                        <Box
                          key={`${imageUrl}-${index}`}
                          borderWidth={index === 0 ? "2px" : "1px"}
                          borderColor={index === 0 ? "teal.400" : "gray.200"}
                          borderRadius="xl"
                          overflow="hidden"
                          bg="gray.50"
                        >
                          <Image
                            src={imageUrl}
                            fallbackSrc={FALLBACK_PRODUCT_IMAGE}
                            alt={`Current product image ${index + 1}`}
                            w="100%"
                            h="120px"
                            objectFit="contain"
                          />
                          <Box p={2}>
                            <Badge
                              colorScheme={index === 0 ? "teal" : "gray"}
                              fontSize="9px"
                            >
                              {index === 0
                                ? "Current main"
                                : `Image ${index + 1}`}
                            </Badge>
                          </Box>
                        </Box>
                      ))}
                    </SimpleGrid>
                  </Box>
                ) : (
                  <Flex
                    mt={4}
                    minH="150px"
                    borderWidth="1px"
                    borderStyle="dashed"
                    borderRadius="xl"
                    align="center"
                    justify="center"
                    direction="column"
                    gap={2}
                    color="gray.500"
                  >
                    <ImagePlus size={30} />
                    <Text fontSize="sm">No saved product images.</Text>
                  </Flex>
                )}
              </Box>

              <FormControl isRequired>
                <FormLabel>Title</FormLabel>
                <Input
                  value={editForm.title}
                  onChange={(event) =>
                    setEditForm((current) => ({
                      ...current,
                      title: event.target.value,
                    }))
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={editForm.description}
                  onChange={(event) =>
                    setEditForm((current) => ({
                      ...current,
                      description: event.target.value,
                    }))
                  }
                  rows={5}
                  resize="vertical"
                />
              </FormControl>

              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>Brand</FormLabel>
                  <Input
                    value={editForm.brand}
                    onChange={(event) =>
                      setEditForm((current) => ({
                        ...current,
                        brand: event.target.value,
                      }))
                    }
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Category</FormLabel>
                  <Input
                    value={editForm.category}
                    onChange={(event) =>
                      setEditForm((current) => ({
                        ...current,
                        category: event.target.value,
                      }))
                    }
                  />
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Price (MWK)</FormLabel>
                  <NumberInput
                    min={1}
                    precision={0}
                    value={editForm.price}
                    onChange={(_value, numberValue) =>
                      setEditForm((current) => ({
                        ...current,
                        price: Number.isFinite(numberValue) ? numberValue : 0,
                      }))
                    }
                  >
                    <NumberInputField />
                  </NumberInput>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Stock quantity</FormLabel>
                  <NumberInput
                    min={0}
                    precision={0}
                    value={editForm.stockQty}
                    onChange={(_value, numberValue) =>
                      setEditForm((current) => ({
                        ...current,
                        stockQty: Number.isFinite(numberValue)
                          ? numberValue
                          : 0,
                      }))
                    }
                  >
                    <NumberInputField />
                  </NumberInput>
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <Select
                    value={editForm.status}
                    onChange={(event) =>
                      setEditForm((current) => ({
                        ...current,
                        status: event.target.value as ProductStatus,
                      }))
                    }
                  >
                    <option value="active">Active</option>
                    <option value="out_of_stock">Out of stock</option>
                    <option value="draft">Draft</option>
                    <option value="archived">Archived</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Featured product</FormLabel>
                  <Flex
                    minH="40px"
                    align="center"
                    justify="space-between"
                    borderWidth="1px"
                    borderRadius="md"
                    px={3}
                  >
                    <Text fontSize="sm">
                      {editForm.isFeatured ? "Featured" : "Standard"}
                    </Text>
                    <Switch
                      colorScheme="teal"
                      isChecked={editForm.isFeatured}
                      onChange={(event) =>
                        setEditForm((current) => ({
                          ...current,
                          isFeatured: event.target.checked,
                        }))
                      }
                    />
                  </Flex>
                </FormControl>
              </SimpleGrid>
            </VStack>
          </ModalBody>

          <ModalFooter gap={3}>
            <Button onClick={closeEdit} isDisabled={savingEdit}>
              Cancel
            </Button>
            <Button
              colorScheme="teal"
              leftIcon={<Edit3 size={16} />}
              onClick={() => void handleSaveEdit()}
              isLoading={savingEdit}
              loadingText="Uploading and saving"
            >
              Save changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={runningSync || Boolean(activeRunId)}
        onClose={() => undefined}
        closeOnOverlayClick={false}
        closeOnEsc={false}
        isCentered
      >
        <ModalOverlay backdropFilter="blur(6px)" />
        <ModalContent borderRadius="2xl" mx={3}>
          <ModalBody py={10}>
            <VStack spacing={5}>
              <GlobalSpinner />
              <Heading size="md" textAlign="center">
                Updating marketplace products
              </Heading>
              <Text textAlign="center" color="gray.500">
                {liveRun?.progressMessage ||
                  "Searching marketplaces and validating current product data…"}
              </Text>
              <Progress
                value={
                  Number(liveRun?.progressTotal ?? 0) > 0
                    ? (Number(liveRun?.progressCurrent ?? 0) /
                        Number(liveRun?.progressTotal ?? 1)) *
                      100
                    : undefined
                }
                isIndeterminate={Number(liveRun?.progressTotal ?? 0) <= 0}
                w="100%"
                borderRadius="full"
              />
              <Text fontSize="sm">
                {liveRun?.progressCurrent ?? 0} /{" "}
                {liveRun?.progressTotal ?? limit}
              </Text>
              <Alert status="info" borderRadius="lg">
                <AlertIcon />
                <Text fontSize="sm">
                  Prices, stock, descriptions and images are checked before
                  products are saved.
                </Text>
              </Alert>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

const ResultStat = ({ label, value }: { label: string; value: number }) => (
  <Stat borderWidth="1px" borderRadius="xl" p={3} minW={0}>
    <StatLabel fontSize="xs" noOfLines={1}>
      {label}
    </StatLabel>
    <StatNumber fontSize="xl">{value}</StatNumber>
  </Stat>
);

const RunHistoryCard = ({ run }: { run: MarketplaceRun }) => (
  <Box borderWidth="1px" borderRadius="xl" p={3.5} minW={0}>
    <Flex justify="space-between" align="flex-start" gap={3}>
      <Box minW={0}>
        <HStack spacing={2} flexWrap="wrap">
          <Text fontWeight="semibold" textTransform="capitalize">
            {(run.mode || "sync").replaceAll("_", " ")}
          </Text>
          <Badge
            colorScheme={getRunStatusColor(run.status)}
            borderRadius="full"
            textTransform="capitalize"
          >
            {run.status || "unknown"}
          </Badge>
        </HStack>

        <Text mt={1} fontSize="sm" color="gray.500" noOfLines={1}>
          {run.queries?.join(", ") || "No search queries"}
        </Text>
      </Box>

      <Text fontSize="10px" color="gray.500" whiteSpace="nowrap">
        {formatDate(run.createdAt)}
      </Text>
    </Flex>

    <HStack mt={3} spacing={2} flexWrap="wrap">
      <Badge variant="subtle" borderRadius="full">
        Found {run.discovered || 0}
      </Badge>
      <Badge variant="subtle" colorScheme="green" borderRadius="full">
        Created {run.created || 0}
      </Badge>
      <Badge variant="subtle" colorScheme="blue" borderRadius="full">
        Updated {run.updated || 0}
      </Badge>
      {run.failed > 0 && (
        <Badge variant="subtle" colorScheme="red" borderRadius="full">
          Failed {run.failed}
        </Badge>
      )}
    </HStack>

    {run.errors?.[0] && (
      <Text mt={2} fontSize="xs" color="red.500" noOfLines={1}>
        {run.errors[0]}
      </Text>
    )}
  </Box>
);

export default MarketplaceSyncPage;
